import { UseQueryOptions, useQuery } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { AxiosError } from 'axios'
import { fetch } from '@dhl/fetch'
import { getTopLevelDomain } from '~/config'

export enum PaymentTypes {
  'Ideal' = 'Ideal',
  'Visa' = 'Visa',
  'MasterCard' = 'MasterCard',
  'CreditCard' = 'CreditCard',
  'Swish' = 'Swish',
}

export type Ideal = { type: PaymentTypes.Ideal; usePaymentService?: boolean; issuers?: { id: string; name: string }[] }
type Visa = { type: PaymentTypes.Visa; usePaymentService?: boolean }
type MasterCard = { type: PaymentTypes.MasterCard; usePaymentService?: boolean }
type CreditCard = { type: PaymentTypes.CreditCard; usePaymentService?: boolean; brands: { name: 'visa' | 'mc' } }
type Swish = { type: PaymentTypes.Swish; usePaymentService?: boolean }

export type SupportedPaymentMethod = Ideal | CreditCard | Visa | MasterCard | Swish

type PaymentMethodsResponse = {
  paymentMethods: SupportedPaymentMethod[]
}

type UseFetchSupportedPaymentMethodsArgs = {
  shopperCountryCode: string
  locale: string
}

const getFetchSupportedPaymentMethodsQueryKey = ({ locale, shopperCountryCode }: UseFetchSupportedPaymentMethodsArgs) =>
  `/api/supported-payment-methods?shopperCountryCode=${shopperCountryCode}&locale=${locale}`

const fetchSupportedPaymentMethods = async (data: UseFetchSupportedPaymentMethodsArgs) => {
  const response = await fetch<void, PaymentMethodsResponse>({
    method: 'GET',
    url: getFetchSupportedPaymentMethodsQueryKey(data),
  })

  return response
}

export const useFetchSupportedPaymentMethods = <
  TData = Awaited<ReturnType<typeof fetchSupportedPaymentMethods>>,
  TError = AxiosError,
>(options?: {
  query?: UseQueryOptions<Awaited<ReturnType<typeof fetchSupportedPaymentMethods>>, TError, TData>
}) => {
  const { i18n } = useTranslation()

  const data: UseFetchSupportedPaymentMethodsArgs = {
    locale: i18n.language,
    shopperCountryCode: getTopLevelDomain().toUpperCase(),
  }

  return useQuery({
    queryKey: [getFetchSupportedPaymentMethodsQueryKey(data)],
    queryFn: () => fetchSupportedPaymentMethods(data),
    ...(options?.query ?? {}),
  })
}
